// src/pages/seasons/AttendanceList.jsx
'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  doc, getDoc, collection, query, where, orderBy,
  onSnapshot, addDoc, serverTimestamp, getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '@/firebase/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import GooglePlacesInput from '@/components/GooglePlacesInput';
import useNudgeCooldown from '@/hooks/useNudgeCooldown';

import {
  ArrowLeftIcon,
  PlusIcon,
  ClipboardDocumentCheckIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  CloudIcon,
  LinkIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const APP_BASE = import.meta.env.VITE_APP_URL || 'https://app.getscrimly.com';

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */
const SESSION_TYPES = ['Practice', 'Game', 'Friendly', 'Tournament', 'Other'];

const TYPE_META = {
  Practice:   { emoji: '⚽', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Game:       { emoji: '🏆', color: 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40' },
  Friendly:   { emoji: '🥅', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  Tournament: { emoji: '🎯', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  Other:      { emoji: '📋', color: 'bg-zinc-50 text-zinc-600 border-zinc-200' },
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate)   return v.toDate();
  if (v?.seconds)  return new Date(v.seconds * 1000);
  return new Date(v);
}

function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  return new Date(dateStr + 'T' + (timeStr || '00:00') + ':00');
}

function formatTime(timeStr) {
  if (!timeStr) return null;
  try {
    const [h, min] = timeStr.split(':');
    const t = new Date();
    t.setHours(+h, +min);
    return t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return timeStr; }
}

/* ------------------------------------------------------------------ */
/* Location picker                                                      */
/* ------------------------------------------------------------------ */
function LocationPicker({ teamId, value, onChange }) {
  const [savedFields, setSavedFields] = useState([]);
  const [mode, setMode] = useState('saved'); // 'saved' | 'custom'

  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'teams', teamId, 'fields'),
      orderBy('name', 'asc'),
    );
    return onSnapshot(q, snap => {
      setSavedFields(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [teamId]);

  const handleSavedSelect = (e) => {
    const fieldId = e.target.value;
    if (!fieldId) { onChange(null); return; }
    const field = savedFields.find(f => f.id === fieldId);
    if (!field) return;
    onChange({
      fieldId:   field.id,
      name:      field.name,
      address:   field.address,
      lat:       field.lat,
      lng:       field.lng,
      isIndoor:  field.isIndoor  || false,
      fieldType: field.fieldType || 'other',
    });
  };

  const handleCustomPlace = useCallback((place) => {
    if (!place) { onChange(null); return; }
    onChange({
      fieldId:   null,
      name:      place.name,
      address:   place.address,
      lat:       place.lat,
      lng:       place.lng,
      placeId:   place.placeId,
      isIndoor:  false,
      fieldType: 'other',
    });
  }, [onChange]);

  return (
    <div className="space-y-3">
      {/* Mode tabs */}
      <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden text-xs font-bold uppercase">
        {[
          { key: 'saved',  label: '📍 Saved Fields' },
          { key: 'custom', label: '🔍 Search' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => { setMode(key); onChange(null); }}
            className={clsx(
              'px-3 py-1.5 transition-colors',
              mode === key
                ? 'bg-[#0d1f14] text-white'
                : 'bg-white text-zinc-600 hover:bg-[#b5f03d]/10',
            )}
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Saved fields dropdown */}
      {mode === 'saved' && (
        savedFields.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 px-3 py-2.5 text-xs text-zinc-500">
            <MapPinIcon className="h-4 w-4 shrink-0" />
            <span>
              No saved fields yet.{' '}
              {teamId && (
                <Link
                  to={`/teams/${teamId}/fields`}
                  className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] hover:underline"
                >
                  Add your team fields →
                </Link>
              )}
            </span>
          </div>
        ) : (
          <select
            onChange={handleSavedSelect}
            defaultValue=""
            className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
          >
            <option value="">— Select a field —</option>
            {savedFields.map(f => (
              <option key={f.id} value={f.id}>
                {f.isIndoor ? '🏟️' : f.fieldType === 'turf' ? '🟩' : '🌿'} {f.name}
              </option>
            ))}
          </select>
        )
      )}

      {/* Google Places search */}
      {mode === 'custom' && (
        <GooglePlacesInput
          onPlace={handleCustomPlace}
          placeholder="Search for a park, school, sports complex…"
        />
      )}

      {/* Confirmed location chip */}
      {value?.lat && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
          <CheckCircleIcon className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-emerald-800 truncate">{value.name}</p>
            <p className="text-[11px] text-emerald-600 truncate">{value.address}</p>
            {value.isIndoor && (
              <p className="mt-0.5 text-[10px] font-medium text-[#5a8a1f]">
                Indoor — weather alerts disabled
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* RSVP nudge row — shown above an upcoming session whose response      */
/* rate is below threshold. Wraps useNudgeCooldown so coaches don't     */
/* spam-fire SMS within 5 minutes. Calls the existing sendRsvpNudge     */
/* Cloud Function — no new server work added.                           */
/* ------------------------------------------------------------------ */
function RsvpNudgeRow({ session, dateLabel, responses, rosterSize }) {
  // sendRsvpNudge takes a `gameId` (the schedule event). Only render
  // when the session is linked to a schedule event we can address.
  const eventId = session.scheduleEventId;
  const callable = useMemo(
    () => httpsCallable(getFunctions(app, 'us-central1'), 'sendRsvpNudge'),
    [],
  );
  // Non-responder count = roster - responses (best estimate from this page's data)
  const nonResponderCount = Math.max(0, rosterSize - responses);
  const nudge = useNudgeCooldown(eventId, nonResponderCount, callable);

  if (!eventId) return null;

  const venueLabel = session.location?.name ? ` · ${session.location.name}` : '';
  const timeLabel  = session.time ? formatTime(session.time) : '';

  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]"
    >
      <BellAlertIcon className="h-5 w-5 shrink-0 text-amber-600" />
      <p className="flex-1 min-w-0 text-sm text-amber-900">
        <span className="font-bold">{session.type || 'Session'} {dateLabel}</span>
        {timeLabel ? ` ${timeLabel}` : ''}
        {venueLabel}
        <span className="block text-xs text-amber-700 mt-0.5">
          Only {responses} of {rosterSize} RSVPs
        </span>
      </p>
      <button
        type="button"
        onClick={nudge.send}
        disabled={nudge.disabled}
        aria-label="Send RSVP nudge"
        className="inline-flex items-center gap-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed text-white px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors shrink-0"
        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
      >
        <BellAlertIcon className="h-4 w-4" />
        {nudge.sending ? 'Sending…' : 'Send a nudge'}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */
export default function AttendanceList() {
  const { seasonId } = useParams();
  const navigate     = useNavigate();

  const [season,     setSeason]     = useState(null);
  const [sessions,   setSessions]   = useState([]);
  const [rsvpCounts, setRsvpCounts] = useState({}); // { sessionId: { going, maybe, not_going } }
  const [rosterSize, setRosterSize] = useState(0);
  const [loading,    setLoading]    = useState(true);

  // Form state
  const [showForm,    setShowForm]    = useState(false);
  const [newDate,     setNewDate]     = useState('');
  const [newTime,     setNewTime]     = useState('');
  const [newType,     setNewType]     = useState('Practice');
  const [newLocation, setNewLocation] = useState(null);
  const [newNote,     setNewNote]     = useState('');
  const [saving,      setSaving]      = useState(false);

  /* Load season */
  useEffect(() => {
    getDoc(doc(db, 'seasons', seasonId)).then(snap => {
      if (snap.exists()) setSeason({ id: snap.id, ...snap.data() });
    });
  }, [seasonId]);

  /* Live sessions */
  useEffect(() => {
    const q = query(
      collection(db, 'sessions'),
      where('seasonId', '==', seasonId),
      orderBy('date', 'desc'),
    );
    return onSnapshot(q, snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [seasonId]);

  /* Load RSVP counts per session (runs once sessions load) */
  useEffect(() => {
    if (!sessions.length) return;
    const q = query(collection(db, 'rsvps'), where('seasonId', '==', seasonId));
    return onSnapshot(q, snap => {
      const counts = {};
      snap.docs.forEach(d => {
        const { sessionId: sid, status } = d.data();
        if (!counts[sid]) counts[sid] = { going: 0, maybe: 0, not_going: 0 };
        if (counts[sid][status] !== undefined) counts[sid][status]++;
      });
      setRsvpCounts(counts);
    });
  }, [seasonId, sessions.length]);

  /* Load roster size — count of active (confirmed/paid) registrations for
   * this season. Used to compute RSVP response rate so we know whether a
   * session needs a nudge. teamId filter is required by Firestore rules. */
  useEffect(() => {
    if (!season?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId',   '==', season.teamId),
      where('seasonId', '==', seasonId),
      where('status', 'in', ['confirmed', 'paid']),
    );
    return onSnapshot(q, snap => setRosterSize(snap.size), () => setRosterSize(0));
  }, [season?.teamId, seasonId]);

  const resetForm = () => {
    setNewDate(''); setNewTime(''); setNewType('Practice');
    setNewLocation(null); setNewNote(''); setShowForm(false);
  };

  const createSession = async (e) => {
    e.preventDefault();
    if (!newDate) { toast.error('Pick a date.'); return; }
    setSaving(true);
    try {
      const dateTime = combineDateTime(newDate, newTime);
      const ref = await addDoc(collection(db, 'sessions'), {
        seasonId,
        teamId:    season?.teamId ?? null,
        date:      dateTime,
        time:      newTime   || null,
        type:      newType,
        location:  newLocation || null,
        note:      newNote.trim() || null,
        createdAt: serverTimestamp(),
      });
      toast.success('Session created.');
      resetForm();
      navigate(`/seasons/${seasonId}/attendance/${ref.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create session.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={season?.teamId} />

        {/* Header */}
        <div className="mb-6">
          {/* Back goes to the per-team seasons list (which has TeamSubNav)
              instead of the global /seasons (which doesn't). Falls back to
              global if season hasn't loaded yet. */}
          <button
            onClick={() => navigate(season?.teamId ? `/teams/${season.teamId}/seasons` : '/seasons')}
            className="mb-3 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back to Seasons
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}
              >
                {season?.name ?? 'Season'}
              </p>
              <h1
                className="uppercase text-[#0d1f14] leading-[1.05]"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 32 }}
              >
                Schedule
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {season?.teamId && (
                <Link
                  to={`/teams/${season.teamId}/fields`}
                  className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />
                  Manage Fields
                </Link>
              )}
              <button
                type="button"
                onClick={() => setShowForm(v => !v)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <PlusIcon className="h-4 w-4" />
                Add to schedule
              </button>
            </div>
          </div>
        </div>

        {/* New session form */}
        {showForm && (
          <form
            onSubmit={createSession}
            className="mb-6 rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-5"
          >
            <h2
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Add to schedule
            </h2>

            {/* Date / Time / Type */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#0d1f14]">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  required
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
                />
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-[#0d1f14]">
                  <ClockIcon className="h-3.5 w-3.5" /> Time
                </label>
                <input
                  type="time"
                  value={newTime}
                  onChange={e => setNewTime(e.target.value)}
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#0d1f14]">Type</label>
                <select
                  value={newType}
                  onChange={e => setNewType(e.target.value)}
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
                >
                  {SESSION_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="mb-2 flex items-center gap-1 text-xs font-semibold text-[#0d1f14]">
                <MapPinIcon className="h-3.5 w-3.5" />
                Location
                <span className="font-normal text-zinc-400">(used for weather alerts)</span>
              </label>
              <LocationPicker
                teamId={season?.teamId}
                value={newLocation}
                onChange={setNewLocation}
              />
            </div>

            {/* Note */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#0d1f14]">
                Note <span className="font-normal text-zinc-400">(optional)</span>
              </label>
              <input
                type="text"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="e.g. Bring pinnies, away kit required"
                className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
              />
            </div>

            {/* Weather notice */}
            {newLocation?.lat && !newLocation?.isIndoor && newDate && newTime && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-700">
                <CloudIcon className="h-4 w-4 shrink-0" />
                Weather will be monitored automatically. You'll be alerted if dangerous conditions are forecast.
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {saving
                  ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Creating…</>
                  : 'Create & Take Attendance'
                }
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Session list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(n => (
              <div key={n} className="h-16 rounded-xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-12 text-center">
            <ClipboardDocumentCheckIcon className="mx-auto h-10 w-10 text-zinc-300" />
            <h3
              className="mt-4 text-[#0d1f14] uppercase"
              style={{ fontFamily: HEAD_FONT, fontWeight: 700, fontSize: 18 }}
            >
              No sessions yet
            </h3>
            <p className="mt-1 text-sm text-zinc-500">
              Create your first session to start tracking attendance and enable weather alerts.
            </p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] mt-5"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PlusIcon className="h-4 w-4" /> Add to schedule
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => {
              const d    = toDate(s.date);
              const m    = TYPE_META[s.type] || TYPE_META.Other;
              const tStr = formatTime(s.time);
              const rc   = rsvpCounts[s.id] || {};
              const responses = (rc.going || 0) + (rc.maybe || 0) + (rc.not_going || 0);
              const hasRsvps = responses > 0;

              // Show the inline nudge row only for sessions in the next 24h
              // where the response rate is below 60% AND there's a roster to
              // chase. cooldown is enforced inside useNudgeCooldown.
              const now = Date.now();
              const ms  = d ? d.getTime() : 0;
              const within24h = ms > now && (ms - now) <= 24 * 60 * 60 * 1000;
              const responseRate = rosterSize > 0 ? responses / rosterSize : 1;
              const showNudge = within24h && rosterSize > 0 && responseRate < 0.6 && s.scheduleEventId;
              const nudgeDateLabel = d ? format(d, 'EEE MMM d') : '';

              return (
                <React.Fragment key={s.id}>
                {showNudge && (
                  <RsvpNudgeRow
                    session={s}
                    dateLabel={nudgeDateLabel}
                    responses={responses}
                    rosterSize={rosterSize}
                  />
                )}
                <div
                  className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-5 py-4 hover:border-[#b5f03d] hover:shadow-[0_2px_8px_0_rgba(13,31,20,0.06)] transition-all group"
                >
                  {/* Clickable area — schedule-linked sessions go to EventDetailPage */}
                  <button
                    onClick={() =>
                      s.scheduleEventId
                        ? navigate(`/seasons/${seasonId}/schedule/${s.scheduleEventId}`)
                        : navigate(`/seasons/${seasonId}/attendance/${s.id}`)
                    }
                    className="flex items-center gap-4 flex-1 min-w-0 text-left"
                  >
                    {/* Date badge */}
                    <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#3d6010]">
                      <span
                        className="text-[10px] font-bold uppercase leading-none"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                      >
                        {d ? format(d, 'MMM') : '—'}
                      </span>
                      <span
                        className="text-lg leading-none mt-0.5"
                        style={{ fontFamily: HEAD_FONT, fontWeight: 800 }}
                      >
                        {d ? format(d, 'd') : '—'}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={clsx(
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase',
                          m.color,
                        )}
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          {m.emoji} {s.type}
                        </span>
                        {tStr && (
                          <span className="flex items-center gap-0.5 text-xs text-zinc-400">
                            <ClockIcon className="h-3 w-3" /> {tStr}
                          </span>
                        )}
                        {/* Schedule-linked badge */}
                        {s.scheduleEventId && (
                          <span
                            className="flex items-center gap-0.5 rounded-full bg-[#b5f03d]/15 border border-[#b5f03d]/40 px-2 py-0.5 text-[10px] font-bold uppercase text-[#3d6010]"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                          >
                            📅 Schedule
                          </span>
                        )}
                        {/* RSVP summary pill */}
                        {hasRsvps && (
                          <span className="flex items-center gap-1 rounded-full bg-zinc-100 border border-zinc-200 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
                            ✅{rc.going || 0} 🤔{rc.maybe || 0} ❌{rc.not_going || 0}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-zinc-500 truncate">
                        {d ? format(d, 'EEEE, MMMM d, yyyy') : '—'}
                        {s.location?.name ? ` · ${s.location.name}` : ''}
                        {s.note ? ` · ${s.note}` : ''}
                      </p>
                    </div>
                  </button>

                  {/* Right side indicators */}
                  <div className="shrink-0 flex items-center gap-2">
                    {s.location?.lat && !s.location?.isIndoor && (
                      <CloudIcon className="h-4 w-4 text-zinc-300" title="Weather monitored" />
                    )}
                    {s.attendanceCount != null && (
                      <span className="text-sm font-semibold text-[#0d1f14]">
                        {s.presentCount ?? 0}/{s.attendanceCount}
                      </span>
                    )}
                    {/* Copy RSVP links */}
                    <button
                      type="button"
                      title="Copy RSVP links for parents"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Get registrations for this session's season
                        // teamId filter required by Firestore security rules
                        const regsSnap = await getDocs(
                          query(collection(db, 'registrations'),
                            where('teamId',   '==', season?.teamId),
                            where('seasonId', '==', seasonId),
                            where('status', 'in', ['confirmed','paid'])
                          )
                        );
                        const regs = regsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                        if (!regs.length) { toast('No confirmed players yet.'); return; }
                        const dateStr = d ? format(d, 'MMM d') : 'session';
                        const lines = regs.map(r => `${r.playerName}: ${APP_BASE}/rsvp/${s.id}/${r.id}`);
                        const text = `Please RSVP for ${s.type || 'our session'} on ${dateStr}:\n\n${lines.join('\n')}`;
                        navigator.clipboard.writeText(text);
                        toast.success('RSVP links copied!');
                      }}
                      className="p-1.5 rounded-lg text-zinc-300 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                    <ChevronRightIcon className="h-4 w-4 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
